import React, { useMemo, useRef, useState } from "react";
import { Button, Dialog, PlusIcon } from "evergreen-ui";
import { Card, Input, Table, message } from "antd";
import { MDBBox, MDBCol, MDBRow } from "mdbreact";
import moment from "moment";
import { getDatabase, ref } from "firebase/database";
import Firebase from "../../../Firebase/Firebase";
import FireFetch from "../../../Firebase/FireFetch";
import { useListVals, useObjectVal } from "../../../Firebase/databaseHooks";
import TableActionMenu from "../../../Common/TableActionMenu";
import UiStringActionLayout from "../ModalActionLayout/UiStringActionLayout";
import {
	exportUiStringsSheet,
	importUiStringsFile,
	saveTalmidsUpdates,
} from "../../../utils/translationSheets";

var db = getDatabase(Firebase);

// English master list of app UI strings. Every language is translated from this list.
const UiStringsLayout = (props) => {
	const canCreate = props.canCreate !== false;
	const canUpdate = props.canUpdate !== false;
	const canDelete = props.canDelete !== false;

	const [uiStrings, loading] = useListVals(ref(db, "talmidsdb/uiStrings"));
	const [translations] = useObjectVal(ref(db, "talmidsdb/translations"));
	const [search, setSearch] = useState("");
	const [showModal, setShowModal] = useState(false);
	const [editing, setEditing] = useState(null);
	const [importing, setImporting] = useState(false);
	const fileInput = useRef(null);

	const stringList = useMemo(() => (Array.isArray(uiStrings) ? uiStrings.filter(Boolean) : []), [uiStrings]);

	const openForm = (uiString) => {
		setEditing(uiString || null);
		setShowModal(true);
	};

	const handleDelete = (uiString) => {
		if (!confirm(`Delete "${uiString.key}"? Its translations in every language will also be removed.`)) return;

		const updates = { [`uiStrings/${uiString.id}`]: null };
		Object.keys(translations || {}).forEach((languageId) => {
			if (translations[languageId]?.[uiString.id]) {
				updates[`translations/${languageId}/${uiString.id}`] = null;
			}
		});

		saveTalmidsUpdates(updates, ["talmidsdb/uiStrings", "talmidsdb/translations"])
			.then(() => {
				FireFetch.addLog("uiStrings", uiString.id, "Delete");
				message.success("UI string deleted");
			})
			.catch((error) => message.error("Unable to delete: " + error.message));
	};

	const handleBulkImport = (e) => {
		const file = e.target.files?.[0];
		e.target.value = "";
		if (!file) return;

		setImporting(true);
		importUiStringsFile(file, stringList)
			.then((summary) => {
				if (summary.error) {
					message.error(summary.error);
					return;
				}
				const invalid = summary.invalid.length ? ` · ${summary.invalid.length} invalid row(s) skipped` : "";
				message.success(`${summary.created} added · ${summary.updated} updated · ${summary.unchanged} unchanged${invalid}`, 6);
				FireFetch.addLog("uiStrings", "bulk", "Import");
			})
			.catch((error) => message.error("Unable to import: " + error.message))
			.finally(() => setImporting(false));
	};

	const columns = [
		{
			title: "Key",
			dataIndex: "key",
			key: "key",
			sorter: (a, b) => String(a.key || "").localeCompare(String(b.key || "")),
			defaultSortOrder: "ascend",
		},
		{ title: "English", dataIndex: "english", key: "english" },
		{
			title: "Module",
			dataIndex: "module",
			key: "module",
			sorter: (a, b) => String(a.module || "").localeCompare(String(b.module || "")),
		},
		{
			title: "Updated",
			dataIndex: "updatedAt",
			key: "updatedAt",
			sorter: (a, b) => (a.updatedAt || 0) - (b.updatedAt || 0),
			render: (value) => (value ? moment(value).format("YYYY-MM-DD HH:mm") : "-"),
		},
		{
			title: "Action",
			key: "action",
			render: (_, item) => (
				<TableActionMenu
					items={[
						canUpdate && { key: "edit", label: "Edit", onClick: () => openForm(item) },
						canDelete && { key: "delete", label: "Delete", danger: true, onClick: () => handleDelete(item) },
					]}
				/>
			),
		},
	];

	const term = search.trim().toLowerCase();
	const dataSource = stringList
		.filter((item) =>
			!term ||
			[item.key, item.english, item.module].some((value) => String(value || "").toLowerCase().includes(term))
		)
		.map((item) => ({ ...item, rowKey: item.id }));

	return (
		<div className="font-Nunito">
			<Dialog
				isShown={showModal}
				title={editing ? "Edit UI String" : "New UI String"}
				onCloseComplete={() => setShowModal(false)}
				hasFooter={false}
			>
				<MDBBox>
					<MDBRow>
						<MDBCol md={12} className="d-flex flex-column">
							{showModal && (
								<UiStringActionLayout
									uiString={editing}
									uiStrings={stringList}
									canCreate={canCreate}
									canUpdate={canUpdate}
									callback={() => setShowModal(false)}
								/>
							)}
						</MDBCol>
					</MDBRow>
				</MDBBox>
			</Dialog>
			<input
				ref={fileInput}
				type="file"
				accept=".xlsx, .xls, .csv"
				style={{ display: "none" }}
				onChange={handleBulkImport}
			/>
			<div className="d-flex align-items-center flex-wrap">
				{canCreate && (
					<Button appearance="primary" className="ml-3 my-2" onClick={() => openForm(null)}>
						New String <PlusIcon />
					</Button>
				)}
				{canCreate && (
					<Button className="mx-2" isLoading={importing} onClick={() => fileInput.current?.click()}>
						Bulk Import (Key | English | Module)
					</Button>
				)}
				<Button className="mx-2" onClick={() => exportUiStringsSheet(stringList)}>
					Export English
				</Button>
				<Input.Search
					allowClear
					placeholder="Search key, English or module"
					style={{ maxWidth: 320 }}
					className="mx-2"
					onChange={(e) => setSearch(e.target.value)}
				/>
			</div>
			<Card className="d-flex flex-column">
				<Table
					style={{ overflow: "auto" }}
					rowKey="rowKey"
					loading={loading}
					pagination={{ defaultPageSize: 100, showSizeChanger: true, pageSizeOptions: ["20", "50", "100"] }}
					dataSource={dataSource}
					columns={columns}
				/>
			</Card>
		</div>
	);
};

export default UiStringsLayout;
